import { AppSettings } from "@/AppSettings";
import { ConfigurationSourceDataMap, SourceDataFieldType } from "@/api/dtos";
import MetadataContext from "@/contexts/data/MetadataContext";
import TrialContext from "@/contexts/data/TrialContext";
import useEventConfiguration from "@/hooks/form/configuration/useEventConfiguration";
import useFormConfiguration from "@/hooks/form/configuration/useFormConfiguration";
import useFormVersionConfiguration from "@/hooks/form/configuration/useFormVersionConfiguration";
import useItemConfiguration from "@/hooks/form/configuration/useItemConfiguration";
import useItemGroupConfiguration from "@/hooks/form/configuration/useItemGroupConfiguration";
import useItemValueConfiguration from "@/hooks/form/configuration/useItemValueConfiguration";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, Chip, Divider, ListItem, ListItemButton, ListItemIcon, ListItemText, Paper, Tooltip, chipClasses, listItemButtonClasses, listItemClasses, listItemSecondaryActionClasses, styled } from "@mui/material";
import { useScopedField } from "@ngt/forms-core";
import { FC, useContext, useLayoutEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import ConfigurationSourceDataFormLinkChip from "./ConfigurationSourceDataFormLinkChip";
import sourceDataFieldTypeToText from "@/utilities/configuration/sourceDataFieldTypeToText";
import sourceDataItemGroupTypeToText from "@/utilities/configuration/sourceDataItemGroupTypeToText";

export const ActionButton = styled(Button)(({ theme }) => ({ 
    borderRadius: 0,
    fontSize: '1rem',
    minWidth: theme.spacing(6)
}));

export const ActionButtonBox = styled(Box)(({ theme }) => ({ 
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'space-between',
    minWidth: theme.spacing(8),
    gap: theme.spacing(0)
}));

export const ActionBox = styled(Box)(({ theme }) => ({
    top: 0, 
    bottom: 0, 
    height: theme.spacing(6), 
    display: 'flex', 
    marginRight: theme.spacing(-2),
    gap: theme.spacing(2),
    padding: theme.spacing(0, 2)
}));

export const TypeBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(0),
    minWidth: theme.spacing(16.875),
    //background: theme.palette.mode === 'dark' ? theme.palette.primary.dark : theme.palette.primary.light,
    //color: theme.palette.common.white,
    alignSelf: 'stretch',
    margin: theme.spacing(0),
    display: 'flex',
    alignItems: 'center',

    [`& > .${chipClasses.root}`]: {
        height: theme.spacing(3),
    },
}));

export const LinkBox = styled(Box)(({ theme }) => ({
    margin: theme.spacing(0),
    //background: theme.palette.mode === 'dark' ? theme.palette.primary.dark : theme.palette.primary.light,
    //color: theme.palette.common.white,
    alignSelf: 'stretch',
    //margin: theme.spacing(0),
    display: 'flex',
    alignItems: 'center',
    minWidth: theme.spacing(8),
    justifyContent: 'flex-end',

    '& > div': {
        minWidth: theme.spacing(6),
    }
}));

export const DetailsListItem = styled(ListItem)(({ theme }) => ({
    [`& > .${listItemButtonClasses.root}`]: {
        paddingRight: theme.spacing(0)
    },
    [`& > .${listItemSecondaryActionClasses.root}`]: {
        right: theme.spacing(0)
    }
}));

export const DetailsListItemButton = styled(ListItemButton)(({ theme }) => ({
    padding: theme.spacing(0, 2),
    minHeight: theme.spacing(6)
}));

export const DetailsListItemText = styled(ListItemText)(({ theme }) => ({
    margin: theme.spacing(0.5, 0)
}));

export const DetailsListItemIcon = styled(ListItemIcon)(({ theme }) => ({
    minWidth: theme.spacing(6)
}));

const FormContainer = styled(Paper)(({ theme }) => ({ 
    marginBottom: theme.spacing(2)
}));

export const ConfigurationSourceDataFormEventDetails: FC<{ eventData?: ReturnType<typeof useEventConfiguration>; divider?: boolean; }> = ({
    eventData,
    divider
}) => {

    if (!eventData) {
        return null;
    }

    return (
        <>
            <DetailsListItem 
                disablePadding
                secondaryAction={
                    <ActionBox>
                        <ActionButtonBox>
                            <Tooltip 
                                title="Forms"
                                placement="left"
                            >
                                <ActionButton
                                    // @ts-ignore-next-line 
                                    component={Link} 
                                    to={eventData.configurationFormsLink} 
                                    color="info"
                                    disableElevation
                                >
                                    <FontAwesomeIcon icon={AppSettings.icons.form} size="lg" />
                                </ActionButton>
                            </Tooltip>
                        </ActionButtonBox>
                        <LinkBox>
                            <ConfigurationSourceDataFormLinkChip 
                                type={eventData.type}
                                linked={eventData.linked}
                            />
                        </LinkBox>
                    </ActionBox>
                }
            >
                <DetailsListItemButton 
                    //@ts-ignore-next-line
                    component={Link} 
                    to={eventData.configurationLink}
                >
                    <DetailsListItemIcon>
                        <FontAwesomeIcon fixedWidth size="lg" icon={eventData.icon} />
                    </DetailsListItemIcon>
                    <DetailsListItemText 
                        primary={eventData.displayName} 
                    />
                </DetailsListItemButton>
            </DetailsListItem>
            { divider && <Divider /> }
        </>
    );
};

export const ConfigurationSourceDataFormFormDetails: FC<{ formData?: ReturnType<typeof useFormConfiguration>; divider?: boolean; }> = ({
    formData,
    divider
}) => {
    if (!formData) {
        return null;
    }

    return (
        <>
            <DetailsListItem 
                disablePadding
                secondaryAction={
                    <ActionBox>
                        <ActionButtonBox>
                            <Tooltip 
                                title="Form Versions"
                                placement="left"
                            >
                                <ActionButton
                                    // @ts-ignore-next-line 
                                    component={Link} 
                                    to={formData.configurationFormVersionsLink} 
                                    color="info"
                                    disableElevation
                                >
                                    <FontAwesomeIcon icon={AppSettings.icons.formVersion} size="lg" />
                                </ActionButton>
                            </Tooltip>
                        </ActionButtonBox>
                        <LinkBox>
                            <ConfigurationSourceDataFormLinkChip 
                                type={formData.type}
                                linked={formData.linked}
                            />
                        </LinkBox>
                    </ActionBox>
                }
            >
                <DetailsListItemButton 
                    //@ts-ignore-next-line
                    component={Link} 
                    to={formData.configurationLink}
                >
                    <DetailsListItemIcon>
                        <FontAwesomeIcon fixedWidth size="lg" icon={formData.icon} />
                    </DetailsListItemIcon>
                    <DetailsListItemText 
                        primary={formData.displayName} 
                    />
                </DetailsListItemButton>
            </DetailsListItem>
            { divider && <Divider /> }
        </>
    );
};

export const ConfigurationSourceDataFormFormVersionDetails: FC<{ formVersionData?: ReturnType<typeof useFormVersionConfiguration>; divider?: boolean; }> = ({
    formVersionData,
    divider
}) => {
    if (!formVersionData) {
        return null;
    }

    return (
        <>
            <DetailsListItem 
                disablePadding
                secondaryAction={
                    <ActionBox>
                        <ActionButtonBox>
                            <Tooltip 
                                title="Items"
                                placement="left"
                            >
                                <ActionButton
                                    // @ts-ignore-next-line 
                                    component={Link} 
                                    to={formVersionData.configurationItemsLink} 
                                    color="info"
                                    disableElevation
                                >
                                    <FontAwesomeIcon icon={AppSettings.icons.item} size="lg" />
                                </ActionButton>
                            </Tooltip>
                        </ActionButtonBox>
                        <LinkBox>
                            <ConfigurationSourceDataFormLinkChip 
                                type={formVersionData.type}
                                linked={formVersionData.linked}
                            />
                        </LinkBox>
                    </ActionBox>
                }
            >
                <DetailsListItemButton 
                    //@ts-ignore-next-line
                    component={Link} 
                    to={formVersionData.configurationLink}
                >
                    <DetailsListItemIcon>
                        <FontAwesomeIcon fixedWidth size="lg" icon={formVersionData.icon} />
                    </DetailsListItemIcon>
                    <DetailsListItemText 
                        primary={formVersionData.displayName} 
                    />
                </DetailsListItemButton>
            </DetailsListItem>
            { divider && <Divider /> }
        </>
    );
};

export const ConfigurationSourceDataFormItemGroupDetails: FC<{ itemGroupData?: ReturnType<typeof useItemGroupConfiguration>; divider?: boolean; }> = ({
    itemGroupData,
    divider
}) => {
    if (!itemGroupData) {
        return null;
    }

    return (
        <>
            <DetailsListItem 
                disablePadding
                secondaryAction={
                    <ActionBox>
                        <TypeBox>
                            <Chip color="primary" sx={{width: '100%'}} size="small" label={sourceDataItemGroupTypeToText(itemGroupData.itemGroupMetadata.type)} />
                        </TypeBox>
                        <ActionButtonBox>
                            <Tooltip 
                                title="Items"
                                placement="left"
                            >
                                <ActionButton
                                    // @ts-ignore-next-line 
                                    component={Link} 
                                    to={itemGroupData.configurationItemsLink} 
                                    color="info"
                                    disableElevation
                                >
                                    <FontAwesomeIcon icon={AppSettings.icons.item} size="lg" />
                                </ActionButton>
                            </Tooltip>
                        </ActionButtonBox>
                        <LinkBox>
                            <ConfigurationSourceDataFormLinkChip 
                                type={itemGroupData.type}
                                linked={itemGroupData.linked}
                            />
                        </LinkBox>
                    </ActionBox>
                }
            >
                <DetailsListItemButton 
                    //@ts-ignore-next-line
                    component={Link} 
                    to={itemGroupData.configurationLink}
                >
                    <DetailsListItemIcon>
                        <FontAwesomeIcon fixedWidth size="lg" icon={itemGroupData.icon} />
                    </DetailsListItemIcon>
                    <DetailsListItemText 
                        primary={itemGroupData.displayName} 
                    />
                </DetailsListItemButton>
            </DetailsListItem>
            { divider && <Divider /> }
        </>
    );
};

export const ConfigurationSourceDataFormItemDetails: FC<{ itemData?: ReturnType<typeof useItemConfiguration>; divider?: boolean; }> = ({
    itemData,
    divider
}) => {
    
    const actionRef = useRef<HTMLDivElement>(null)
    const listRef = useRef<HTMLLIElement>(null)
    
    useLayoutEffect(() => {
        if (!actionRef.current || !listRef.current) {
            return;
        }
        
        const width = actionRef.current.getBoundingClientRect().width
        const child = listRef.current.children[0];

        if (!child) return;
        
        (child as HTMLElement).style.paddingRight = `${width + 10}px`
    })

    if (!itemData || itemData.itemMetadata.fieldType === SourceDataFieldType.Hidden) {
        return null;
    }

    return (
        <>
            <DetailsListItem 
                ref={listRef}
                disablePadding
                secondaryAction={
                    <ActionBox ref={actionRef}>
                        <TypeBox>
                            <Chip color="primary" sx={{width: '100%'}} size="small" label={sourceDataFieldTypeToText(itemData.itemMetadata.fieldType)} />
                        </TypeBox>
                        <ActionButtonBox>
                            {
                                itemData.configurationValuesLink && (
                                        <Tooltip 
                                            title="Values"
                                            placement="left"
                                        >
                                            <ActionButton
                                                // @ts-ignore-next-line 
                                                component={Link} 
                                                to={itemData.configurationValuesLink} 
                                                color="info"
                                                disableElevation
                                            >
                                                <FontAwesomeIcon icon={AppSettings.icons.itemValue} size="lg" />
                                            </ActionButton>
                                        </Tooltip>
                                )
                            }
                        </ActionButtonBox>
                        <LinkBox>
                            <ConfigurationSourceDataFormLinkChip 
                                type={itemData.type}
                                linked={itemData.linked}
                            />
                        </LinkBox>
                    </ActionBox>
                }
            >
                <DetailsListItemButton 
                    //@ts-ignore-next-line
                    component={Link} 
                    to={itemData.configurationLink}
                >
                    <DetailsListItemIcon>
                        <FontAwesomeIcon fixedWidth size="lg" icon={itemData.icon} />
                    </DetailsListItemIcon>
                    <DetailsListItemText 
                        primary={itemData.displayName} 
                    />
                </DetailsListItemButton>
            </DetailsListItem>
            { divider && <Divider /> }
        </>
    );
};

export const ConfigurationSourceDataFormItemValueDetails: FC<{ itemValueData?: ReturnType<typeof useItemValueConfiguration>; divider?: boolean; }> = ({
    itemValueData,
    divider
}) => {
    if (!itemValueData) {
        return null;
    }

    return (
        <>
            <DetailsListItem 
                disablePadding
                secondaryAction={
                    <ActionBox>
                        <LinkBox>
                            <ConfigurationSourceDataFormLinkChip 
                                type={itemValueData.type}
                                linked={itemValueData.linked}
                            />
                        </LinkBox>
                    </ActionBox>
                }
            >
                <DetailsListItemButton 
                    //@ts-ignore-next-line
                    component={Link} 
                    to={itemValueData.configurationLink}
                >
                    <DetailsListItemIcon>
                        <FontAwesomeIcon fixedWidth size="lg" icon={itemValueData.icon} />
                    </DetailsListItemIcon>
                    <DetailsListItemText 
                        primary={<>{itemValueData.itemValue} &mdash; {itemValueData.displayName}</>} 
                    />
                </DetailsListItemButton>
            </DetailsListItem>
            { divider && <Divider /> }
        </>
    );
};

interface IConfigurationSourceDataFormDetailsProps {

}

const ConfigurationSourceDataFormDetails: FC<IConfigurationSourceDataFormDetailsProps> = ({

}) => {
    const { 
        studyEventOid, 
        caseReportFormOid, 
        caseReportFormVersionOid,
        itemGroupName,
        itemName,
        itemValue 
    } = useParams();
    const { data: trial } = useContext(TrialContext);
    const { data: metadata } = useContext(MetadataContext);
    const { state: { value: configuration } } = useScopedField<ConfigurationSourceDataMap>('configuration', { value: true });
    const { state: { value: trialConfiguration } } = useScopedField<ConfigurationSourceDataMap>('trialConfiguration', { value: true });

    const configurationSourceDataId = configuration?.configuration?.id;

    const eventData = useEventConfiguration(
        metadata, 
        trialConfiguration ?? undefined, 
        configuration ?? undefined, 
        trial, 
        configurationSourceDataId, 
        studyEventOid
    );

    const formData = useFormConfiguration(
        metadata, 
        trialConfiguration ?? undefined, 
        configuration ?? undefined, 
        trial, 
        configurationSourceDataId, 
        studyEventOid, 
        caseReportFormOid
    );

    const formVersionData = useFormVersionConfiguration(
        metadata, 
        trialConfiguration ?? undefined, 
        configuration ?? undefined, 
        trial, 
        configurationSourceDataId, 
        studyEventOid, 
        caseReportFormOid, 
        caseReportFormVersionOid
    );

    const itemGroupData = useItemGroupConfiguration(
        metadata, 
        trialConfiguration ?? undefined, 
        configuration ?? undefined, 
        trial, 
        configurationSourceDataId, 
        studyEventOid, 
        caseReportFormOid, 
        caseReportFormVersionOid,
        itemGroupName
    );

    const itemData = useItemConfiguration(
        metadata, 
        trialConfiguration ?? undefined, 
        configuration ?? undefined, 
        trial, 
        configurationSourceDataId, 
        studyEventOid, 
        caseReportFormOid, 
        caseReportFormVersionOid,
        itemGroupName,
        itemName
    );

    const itemValueData = useItemValueConfiguration(
        metadata, 
        trialConfiguration ?? undefined, 
        configuration ?? undefined, 
        trial, 
        configurationSourceDataId, 
        studyEventOid, 
        caseReportFormOid, 
        caseReportFormVersionOid,
        itemGroupName,
        itemName,
        itemValue
    );

    const count = 0;

    return (
        <FormContainer>
            <>
                <DetailsListItem 
                    disablePadding
                    secondaryAction={
                        <ActionBox>
                            <ActionButtonBox>
                                <Tooltip 
                                    title="Events"
                                    placement="left"
                                >
                                    <ActionButton
                                        // @ts-ignore-next-line 
                                        component={Link} 
                                        to={`/${trial?.oid}/configuration/source-data/${configuration?.configuration?.isGlobal ? 'trial' : configurationSourceDataId ? configurationSourceDataId : 'new'}/events`} 
                                        color="info"
                                        disableElevation
                                    >
                                        <FontAwesomeIcon icon={AppSettings.icons.event} size="lg" />
                                    </ActionButton>
                                </Tooltip>
                            </ActionButtonBox>
                            <ActionButtonBox>
                                <Tooltip 
                                    title="Forms"
                                    placement="left"
                                >
                                    <ActionButton
                                        // @ts-ignore-next-line 
                                        component={Link} 
                                        to={`/${trial?.oid}/configuration/source-data/${configuration?.configuration?.isGlobal ? 'trial' : configurationSourceDataId ? configurationSourceDataId : 'new'}/forms`} 
                                        color="info"
                                        disableElevation
                                    >
                                        <FontAwesomeIcon icon={AppSettings.icons.form} size="lg" />
                                    </ActionButton>
                                </Tooltip>
                            </ActionButtonBox>
                        </ActionBox>
                    }
                >
                    <DetailsListItemButton
                        //@ts-ignore-next-line
                        component={Link} 
                        to={`/${trial?.oid}/configuration/source-data/${configuration?.configuration?.isGlobal ? 'trial' : configurationSourceDataId ? configurationSourceDataId : 'new'}`}
                    >
                        <DetailsListItemIcon>
                            <FontAwesomeIcon fixedWidth size="lg" icon={AppSettings.icons.sourceData} />
                        </DetailsListItemIcon>
                        <DetailsListItemText 
                            primary={configuration?.configuration?.name} 
                        />
                    </DetailsListItemButton>
                </DetailsListItem>
                {(eventData || formData) && <Divider />}
            </>
            <ConfigurationSourceDataFormEventDetails eventData={eventData} divider={!!formData} />
            <ConfigurationSourceDataFormFormDetails formData={formData} divider={!!formVersionData} /> 
            <ConfigurationSourceDataFormFormVersionDetails formVersionData={formVersionData} divider={!!itemGroupData || !!itemData} /> 
            <ConfigurationSourceDataFormItemGroupDetails itemGroupData={itemGroupData} divider={!!itemData} /> 
            <ConfigurationSourceDataFormItemDetails itemData={itemData} divider={!!itemValueData} /> 
            <ConfigurationSourceDataFormItemValueDetails itemValueData={itemValueData} /> 
        </FormContainer>
    );
};

export default ConfigurationSourceDataFormDetails;